<template>
  <v-container fluid class="discrete-query-page">
    <MissingParamsDialog ref="missingParamsDialog" @mustSaveMissingParams="mustSaveMissingParams = true" />
    <header class="query-page-header">
      <div class="query-page-copy">
        <div class="query-page-eyebrow">Discrete data query</div>
        <h1>Build a data extract</h1>
        <p>Set the data scope, choose analytes and the reporting period, optionally add guidelines, then review the complete selection before downloading.</p>
      </div>
      <div class="query-page-actions">
        <v-btn class="my-2" @click="drawer = true">
          <v-icon left color="info">folder_open</v-icon>
          Load saved query
        </v-btn>
        <NewQuery :confirmOnNew="hasUnsavedChanges" :disabled="!canNewQuery" @clear:query="clearQuery()" />
      </div>
    </header>

    <section class="query-workspace">
      <v-layout>
        <SavedQueries ref="savedQueries" :confirmOnLoad="hasUnsavedChanges" @update:drawer="drawer = $event" :query="query" @load:query="loadQuery($event)" :drawer="drawer" />
        <v-flex class="pt-0" style="min-width: 0;">
          <div class="query-summary">
            <div class="query-summary-copy">
              <strong>{{ query ? query.name : 'New query' }}</strong>
              <span>{{ queryStatusText }}</span>
            </div>
            <SaveQuery buttonLabel="Save as new" :query="query" :disabled="!canSave" @update:query="updateQuery($event)" :selection="selection" :reportOptions="reportOptions" />
          </div>
          <AlertsBar
            class="pb-1"
            :wizardMode="true"
            :mustSaveMissingParams="mustSaveMissingParams"
            @showMissingParams="$refs.missingParamsDialog.show()"
            :hasSelectedStation="hasSelectedStation"
            :hasSelectedAnalyte="hasSelectedAnalyte"
            :hasMadeSelection="hasMadeSelection"
            :isQuerySaved="isQuerySaved"
            :hasQuery="!!query"
            :hasUnsavedChanges="hasUnsavedChanges"
          />
          <v-progress-linear v-if="loading" class="my-0" :height="4" color="info" indeterminate />
          <DiscreteQueryWizard
            ref="queryWizard"
            :isGeneratingReport="isGeneratingReport || isSubmittingAsyncGeneratingRequest"
            @update:isInitialized="isInitialized = $event"
            @update:loading="updateLoadingState"
            @update:selection="updateSelection"
            @update:reportOptions="updateReportOptions"
            @update:allMediaTypes="allMediaTypes = $event"
            @update:canSubmit="queryReady = $event"
            @update:dirty="wizardDirty = $event"
            @update:downloadLoading="isSubmittingAsyncGeneratingRequest = $event"
          />
        </v-flex>
      </v-layout>
    </section>
  </v-container>
</template>

<script>
  import MissingParamsDialog from './MissingParamsDialog'
  import DiscreteQueryWizard from './DiscreteQueryWizard'
  import SaveQuery from './Actions/SaveQuery/SaveQuery'
  import NewQuery from './Actions/NewQuery'

  import SavedQueries from './SavedQueries'
  import AlertsBar from './AlertsBar'



  const serviceUnavailable = (context) => context.$store.dispatch('app/setSnackbar', {
    text: 'Sorry, saved queries are not available right now. Please try again in a few minutes — let us know if it happens again!',
    color: 'error',
    icon: 'error',
  })

  import {
    PING_INTERVAL,
  } from '@/store/ServiceRegistry/services'

  export default {
    components: {
      MissingParamsDialog,
      DiscreteQueryWizard,
      SaveQuery,
      NewQuery,
      SavedQueries,
      AlertsBar,
    },
    data() {
      return {
        isInitialized: false,
        reportOptions: null,
        isGeneratingReport: false,
        isSubmittingAsyncGeneratingRequest: false,
        query: null,
        isQuerySaved: true,
        loadingStates: {},
        selection: null,
        queryReady: false,
        wizardDirty: false,
        drawer: false,

        mustSaveMissingParams: false,
        allMediaTypes: [],
        pingTimeoutId: null,
      }
    },
    computed: {
      isServiceActive() {
        return true
        //return this.$store.getters['multistation/isServiceActive']
      },
      missingParams() {
        return this.$store.getters['multistation/missingParams']
      },
      hasSelectedAnalyte() {
        return this.selection && this.selection.analytes && this.selection.analytes.length > 0
      },
      hasSelectedStation() {
        return this.selection && this.selection.stations && this.selection.stations.length > 0
      },
      hasMadeSelection() {
        return this.hasSelectedAnalyte && this.hasSelectedStation
      },
      hasReportGenerator() {
        return !!this.$store.getters['multistation/reportGenerator']
      },
      canSave() {
        return this.hasReportGenerator && this.queryReady && !this.loading && !this.isSubmittingAsyncGeneratingRequest
      },
      canNewQuery() {
        return !this.loading && !this.isSubmittingAsyncGeneratingRequest
      },
      queryStatusText() {
        if (this.query && !this.wizardDirty) return 'Saved query · Loaded with all selections applied'
        if (this.queryReady && !this.wizardDirty) return 'All steps are complete. Save or download this query.'
        if (this.wizardDirty) return 'Changes are not applied yet. Complete each step to enable saving and downloading.'
        return 'Complete steps 1–4 before saving or downloading.'
      },
      loading() {
        return Object.values(this.loadingStates).some(isLoading => isLoading) || !this.isInitialized
      },
      hasUnsavedChanges() {
        return (this.isServiceActive && this.hasMadeSelection && !this.isQuerySaved) ||
          this.mustSaveMissingParams ||
          this.wizardDirty
      },
    },
    watch: {
      missingParams() {
        this.mustSaveMissingParams = false
      },
    },
    methods: {
      pingService() {
        if (this.pingTimeoutId) return

        let ping = () => {
          this.$store.dispatch('multistation/pingService')
        }

        ping()
        this.pingTimeoutId = setInterval(ping, PING_INTERVAL)
      },
      updateSelection(selection) {
        this.selection = selection
        if (!this.isInitialized || this.loadingStates.selection) return
        this.isQuerySaved = false
      },
      updateReportOptions(options) {
        this.reportOptions = options
      },
      updateLoadingState(states) {
        this.loadingStates = {
          ...this.loadingStates,
          ...states,
        }
        if (states.selection === false && this.wizardDirty) {
          this.isQuerySaved = false
        }
      },
      resolveMediaTypes(queryMediaTypes) {
        if (queryMediaTypes && queryMediaTypes.length) {
          const matched = queryMediaTypes.filter(mt => this.allMediaTypes.includes(mt))
          if (matched.length) return matched
        }
        if (this.allMediaTypes.includes('Water')) return ['Water']
        if (this.allMediaTypes.length) return [this.allMediaTypes[0]]
        return []
      },
      updateQuery(query) {
        this.isQuerySaved = true
        this.mustSaveMissingParams = false
        this.query = query
        if (this.$refs.queryWizard) this.$refs.queryWizard.markCurrentAsSaved()
        this.$refs.savedQueries.fetchQueries()
      },
      async clearQuery() {
        await this.$refs.queryWizard.startNewQuery()
        this.query = null
        this.isQuerySaved = true
        this.queryReady = false
        this.wizardDirty = false
      },
      async fetchReportGenerator() {
        try {
          await this.$store.dispatch('multistation/fetchReportGenerator')
          this.pingService()
        } catch (error) {
          serviceUnavailable(this)
        }
      },
      async loadQuery(query) {
        if (this.loadingStates.selection) return

        const {
          selection,
          reportOptions,
        } = query.queryParams

        const mediaTypes = this.resolveMediaTypes(selection.mediaTypes)

        const selectionOptions = {
          relativeDate: query.relativeDate,
        }

        const restored = await this.$refs.queryWizard.loadSelection({
          ...selection,
          mediaTypes,
        }, selectionOptions, reportOptions)
        if (!restored) return
        this.query = query
        this.isQuerySaved = true
      },
    },
    created() {
      this.fetchReportGenerator()
    },
    beforeDestroy() {
      clearTimeout(this.pingTimeoutId)
    },
  }
</script>

<style>
  .discrete-query-page {
    width: 100%;
    max-width: 1120px !important;
    padding: 24px 28px 56px !important;
  }

  .query-page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 16px;
  }

  .query-page-copy {
    max-width: 700px;
  }

  .query-page-eyebrow {
    margin-bottom: 2px;
    color: #666;
    font-size: 12px;
  }

  .query-page-copy h1 {
    margin: 2px 0 5px;
    color: #292929;
    font-size: 26px;
    font-weight: 400;
    line-height: 1.25;
  }

  .query-page-copy p {
    margin: 14px 0 0;
    color: #666;
    font-size: 14px;
  }

  .query-page-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
  }

  .query-page-actions .v-btn {
    min-height: 38px;
    border: 1px solid rgba(0, 0, 0, .18);
    border-radius: 4px;
    background: #fff !important;
    color: #333;
    font-weight: 500;
    text-transform: none;
  }

  .query-page-actions .v-btn:not(.v-btn--disabled):hover {
    border-color: #777;
    background: #f7f7f7 !important;
  }

  .query-workspace {
    width: 100%;
  }

  .query-summary {
    display: flex;
    min-height: 76px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
    padding: 12px 16px;
    border: 1px solid rgba(0, 0, 0, .14);
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 2px 5px rgba(0, 0, 0, .12);
  }

  .query-summary-copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 3px;
  }

  .query-summary-copy strong {
    overflow: hidden;
    color: #333;
    font-size: 14px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .query-summary-copy span {
    color: #666;
    font-size: 12px;
  }

  .max-width {
    width: 100%;
  }

  .scroll-border-bottom {
    border-bottom-color: #ddd !important;
  }

  .align-button-checkbox {
    padding-left: 14px !important;
  }

  .align-button-checkbox>.v-btn:hover:before {
    background-color: transparent !important;
  }

  .align-button-checkbox-list {
    padding-left: 0px !important;
  }

  .align-button-checkbox-list>.v-btn {
    width: 22px;
    margin-right: 10px !important;
  }

  .align-button-checkbox-list>.v-btn:hover:before {
    background-color: transparent !important;
  }

  @media (max-width: 760px) {
    .discrete-query-page {
      padding: 18px 12px 36px !important;
    }

    .query-page-header {
      flex-direction: column;
      gap: 12px;
    }

    .query-page-actions {
      width: 100%;
      flex-wrap: wrap;
    }

    .query-page-copy h1 {
      font-size: 26px;
    }

    .query-summary {
      align-items: flex-start;
      flex-direction: column;
    }
  }
</style>